import localTreatments from "@/content/treatments.json";

// Shape of a `treatments` collection entry. Mirrors the schema documented in
// docs/cms-collections.md — lib/cms.ts maps CMS entries onto this, and
// content/treatments.json is the fallback when the CMS has nothing.

export type CmsImage = {
  media_id: string;
  alt_text: string;
  /** File name only, resolved against /assets/img until media_id is filled. */
  name: string;
};

export type SectionType = "text" | "list" | "imageText" | "html";

export type TreatmentSection = {
  type: SectionType;
  heading: string;
  paragraphs: string[];
  items: string[];
  image: CmsImage;
  imageSide: "left" | "right";
  /** Raw HTML, only for type "html". */
  html: string;
};

export type Faq = { q: string; a: string };

export type Treatment = {
  slug: string;
  /**
   * "sidebar" (default) renders the standard treatment furniture: doctor
   * sidebar, counter strip, appointment CTA and the shared video pair.
   * "full" is the bare full-width layout the lung-cancer page has always used
   * on the live site — it has none of that.
   */
  layout?: "sidebar" | "full";
  title: string;
  subtitle: string;
  navLabel: string;
  /** Slug of the parent treatment, or "" for a top-level item. */
  parent: string;
  order: number;
  hero_image: CmsImage;
  intro: string[];
  sections: TreatmentSection[];
  faqs: Faq[];
  seoTitle: string;
  metaDescription: string;
  /** 0 = published, 1 = hidden. */
  status: number;
};

export const localArticles = localTreatments as Treatment[];

export function isPublished(t: Treatment): boolean {
  return Number(t.status) === 0;
}

/**
 * Resolves a CMS image field to a URL. Entries store only a file name; until
 * the media is uploaded and media_id filled in, that name is served from the
 * images the site already ships.
 */
export function imageUrl(image: CmsImage | null | undefined): string {
  if (!image) return "";
  if (image.media_id) return `/cms-images/${image.media_id}`;
  if (!image.name) return "";
  return image.name.startsWith("/") ? image.name : `/assets/img/${image.name}`;
}

export type NavNode = {
  label: string;
  href: string;
  children?: NavNode[];
};

/**
 * Flat entries -> menu tree, via the `parent` slug (adjacency list).
 * Throws on an unknown parent or a cycle: silently dropping a page out of the
 * menu is far worse than a failed build, since nobody would notice for weeks.
 */
export function buildTreatmentNav(treatments: Treatment[]): NavNode[] {
  const bySlug = new Map(treatments.map((t) => [t.slug, t]));
  const byParent = new Map<string, Treatment[]>();

  for (const t of treatments) {
    const parent = t.parent || "";
    if (parent && !bySlug.has(parent)) {
      throw new Error(
        `treatment "${t.slug}" has parent "${parent}", which is not a known treatment slug`
      );
    }
    byParent.set(parent, [...(byParent.get(parent) ?? []), t]);
  }

  // Walk each chain to its root to catch cycles before recursing.
  for (const t of treatments) {
    const seen = new Set([t.slug]);
    let cursor = t.parent;
    while (cursor) {
      if (seen.has(cursor)) {
        throw new Error(`treatment parent cycle involving "${t.slug}"`);
      }
      seen.add(cursor);
      cursor = bySlug.get(cursor)?.parent ?? "";
    }
  }

  const level = (parent: string): NavNode[] =>
    (byParent.get(parent) ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((t) => {
        const children = level(t.slug);
        return {
          label: t.navLabel || t.title,
          href: `/${t.slug}`,
          ...(children.length ? { children } : {}),
        };
      });

  return level("");
}
